import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'

export type Lang = 'de' | 'en'

interface LanguageContextValue {
  lang: Lang
  setLang: (lang: Lang) => void
  toggleLang: () => void
}

const STORAGE_KEY = 'flixalio-lang'

/**
 * English is FLIXALIO's source language — every label in the code is its English
 * string, and German comes out of the dictionary. Flixpector runs the same
 * mechanism the other way round (German source, English dictionary); only the
 * direction differs, the switch and the wording line up across both apps.
 */
const DEFAULT_LANG: Lang = 'en'

const LanguageContext = createContext<LanguageContextValue | null>(null)

/**
 * Storage access is wrapped because it is not always there to be used: Safari's
 * private mode throws on write, enterprise policies can block it outright, and
 * jsdom only stubs part of the API. Losing the preference is survivable — a
 * crashing shell is not. index.html guards the theme read the same way.
 */
function readStoredLang(): Lang {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return stored === 'de' || stored === 'en' ? stored : DEFAULT_LANG
  } catch {
    return DEFAULT_LANG
  }
}

function writeStoredLang(lang: Lang): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    // Preference is not remembered across reloads; the session still works.
  }
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(readStoredLang)

  // Screen readers and the browser's own translation prompt read this, and
  // index.html ships a hard-coded lang that would otherwise stay wrong.
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  const value = useMemo<LanguageContextValue>(() => {
    const persist = (next: Lang) => {
      setLangState(next)
      writeStoredLang(next)
    }
    return {
      lang,
      setLang: persist,
      toggleLang: () => {
        persist(lang === 'de' ? 'en' : 'de')
      },
    }
  }, [lang])

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

/**
 * Outside a provider this falls back to the source language rather than
 * throwing: the ErrorBoundary sits above the provider, and unit tests render
 * single components. In that state `t()` is the identity function, so the
 * fallback can only ever show the English source string — never a blank label.
 */
const FALLBACK: LanguageContextValue = {
  lang: DEFAULT_LANG,
  setLang: () => {},
  toggleLang: () => {},
}

export function useLanguage(): LanguageContextValue {
  return useContext(LanguageContext) ?? FALLBACK
}
